<template>
  <with-loader id="conversation-detail"
               class="border-transparent bg-white h-full"
               :loading="isLoading"
               use-as-overlay
  >
    <div class="flex flex-col h-full">
      <div v-if="showUnreleased" class="flex items-center gap-2 p-2 flex-shrink-0">
        <input id="show-debug-steps-toggle"
               type="checkbox"
               data-test="show-debug-steps-toggle"
               v-model="showDebugSteps"
        />
        <base-label id="show-debug-steps-toggle"
                    label="Show debug steps"
                    :is-admin="true"
                    label-classes="text-sm font-medium"
                    class="cursor-pointer"
        />
      </div>
      <div class="flex-1 min-h-0" v-bar>
        <div class="grid grid-cols-5 w-full h-auto max-h-full">
          <template v-for="(message, i) of visibleMessages">
            <conversation-selectable-message
              v-if="['quick_reply', 'postback'].includes(message.type) "
              :key="`clickable-${i}`"
              :message="message"
              :message-index="i"
              :type="message.type"
              :messages="visibleMessages"
            />
            <conversation-client-message
              v-else-if="message.data.direction && message.data.direction.toLowerCase() === MESSAGE_DIRECTION.RECEIVED"
              :ref="message.id"
              :key="`client-${i}`"
              :message="message"
              :message-index="i"
              :bot-id="botId"
              :answers-by-intent-id="answersByIntentId"
              :selected-bubble="selectedBubble"
              @bubble-selected="selectClientMessage"
            />
            <conversation-answer-end
              v-else-if="message.data.type && [
                ANSWER_END.FIND_INTENT,
                ANSWER_END.IDLE,
                ANSWER_END.CONTINUE_ANSWER
              ].includes(message.data.type)"
              :key="`answer-end-${i}`"
              :type="message.data.type"
              :answer-id="message.data.answerId"
              :bot-id="botId"
              :answers-by-id="answersById"
            />
            <conversation-api-call
              v-else-if="message.type === 'remote_action'"
              :key="`remote-action-${i}`"
              :ref="message.id"
              :message="message"
              :selected-bubble="selectedBubble"
              @bubble-selected="selectApiAction(message)"
            />
            <ConversationChatGPTAction
              v-else-if="message.type === 'chat_gpt_action'"
              :key="`chatgpt-action-${i}`"
              :ref="message.id"
              :message="message"
              :selected-bubble="selectedBubble"
              @bubble-selected="selectChatGPTAction(message)"
            />
            <ConversationEvent
              v-else-if="message.type === 'event' || message.type === 'bot_wake_up'"
              :key="`event-${i}`"
              :message="message"
              :selected-bubble="selectedBubble"
              @bubble-selected="selectEvent(message)"
            />
            <ConversationDebugStep
              v-else-if="message.type === 'debug_step'"
              :key="`debug-step-${i}`"
              :message="message"
            />
            <conversation-bot-message
              v-else
              :key="`bot-${i}`"
              :message="message"
              :is-email-bot="isEmailBot"
              :selected-quick-reply-index="getSelectedQuickReplyIndex(message, i)"
            />
          </template>
        </div>
      </div>
    </div>
  </with-loader>
</template>

<script>
import isEmpty from 'ramda/src/isEmpty'
import clone from 'ramda/src/clone'
import values from 'ramda/src/values'
import findIndex from 'ramda/src/findIndex'
import path from 'ramda/src/path'
import move from 'ramda/src/move'
import analyticsService from '@/services/amio/analytics.service'
import WithLoader from '@/components/common/WithLoader.vue'
import requestOutcomeMapper from '@/utils/request-outcome.mapper'
import chartLabels from '@/utils/chart-labels'
import moment from 'moment-timezone'
import toaster from '@/utils/toaster'
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import conversationDetailModule from '@/store/modules/conversation-detail.module'
import datetimeUtils from '@/utils/datetime.utils'
import botModule from '@/store/modules/bot.module'
import errorUtils from '@/utils/error.utils'
import { MESSAGE_DIRECTION } from '@/constants/conversation.constants'
import { ANSWER_END, OUTCOME_TYPES } from '@/utils/bot-json.constants'
import { BOT_TYPES } from '@/utils/bot.constants'
import ConversationClientMessage from '@/components/analytics/conversations/detail/ConversationClientMessage.vue'
import ConversationSelectableMessage
  from '@/components/analytics/conversations/detail/ConversationSelectableMessage.vue'
import ConversationAnswerEnd from '@/components/analytics/conversations/detail/ConversationAnswerEnd.vue'
import ConversationBotMessage from '@/components/analytics/conversations/detail/ConversationBotMessage.vue'
import ConversationApiCall from '@/components/analytics/conversations/detail/ConversationApiCall.vue'
import ConversationChatGPTAction from '@/components/analytics/conversations/detail/ConversationChatGPTAction.vue'
import ConversationEvent from '@/components/analytics/conversations/detail/ConversationEvent'
import ConversationDebugStep from '@/components/analytics/conversations/detail/ConversationDebugStep.vue'
import BaseLabel from '@/components/base-components/BaseLabel.vue'
import EventDeduplicator from '@/utils/event-deduplicator'

export default {
  name: 'ConversationDetail',

  emits: ['update:isLoading', 'client-message-selected', 'remote-action-selected', 'clear-bubble-selected'],

  components: {
    ConversationApiCall,
    ConversationChatGPTAction,
    WithLoader,
    ConversationClientMessage,
    ConversationSelectableMessage,
    ConversationAnswerEnd,
    ConversationBotMessage,
    ConversationEvent,
    ConversationDebugStep,
    BaseLabel
  },

  props: {
    contactId: {
      type: String,
      default: null
    },
    selectedBubble: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    answersByIntentId: {
      type: Object,
      default: () => {}
    },
    answersById: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      MESSAGE_DIRECTION,
      ANSWER_END,
      showDebugSteps: false
    }
  },

  watch: {
    contactId: {
      immediate: true,
      handler: function (newContactId, oldContactId) {
        if (newContactId === oldContactId) return

        this.loadConversation(newContactId)
      }
    },

    isLoading: {
      immediate: false,
      async handler(isLoading) {
        if (isLoading) return

        const routeQueryMessage = this.messages.find(({ id }) => id === this.$route.query.messageId)
        if (routeQueryMessage) {
          switch (routeQueryMessage.type) {
            case 'remote_action':
              await this.selectApiAction(routeQueryMessage)
              break
            case 'chat_gpt_action':
              await this.selectChatGPTAction(routeQueryMessage)
              break
            case 'event':
            case 'bot_wake_up':
              await this.selectEvent(routeQueryMessage)
              break
            default:
              await this.selectClientMessage(routeQueryMessage)
              break
          }
          await this.handleScrollToBubble(routeQueryMessage.id)
        } else {
          const routeQueryRequest = this.messages.find(message => message?.data?.message_id === this.$route.query.requestId)
          const firstRequestMessage = this.messages.find(message => message?.data?.message_id === this.firstRequest?.message_id)
          const selectedRequest = routeQueryRequest ? routeQueryRequest : firstRequestMessage
          if (!selectedRequest) return

          await this.$nextTick()
          this.selectClientMessage(selectedRequest)
          await this.handleScrollToBubble(selectedRequest.id)
        }
      }
    }
  },

  computed: {
    messages: conversationDetailModule.getMessages,
    visibleMessages() {
      return this.showDebugSteps ? this.messages : this.messages.filter(message => message.type !== 'debug_step')
    },
    requests: conversationDetailModule.getRequests,
    bot: analyticsFilterModule.getBot,

    // A conversation (one contact) is always answered by a single bot — resolve it from any
    // of its own requests, instead of the page-level filter bot, which can differ once History
    // is showing conversations aggregated across multiple bots.
    botId() {
      return values(this.requests).find(({ bot_id }) => bot_id)?.bot_id || this.bot?.id || null
    },

    // Prefer the conversation bot type over botModule.isEmailBot (editor bot can be stale
    // when the History filter switches between email and chat).
    isEmailBot() {
      const conversationBot = this.botId ? botModule.getBotList().find(b => b.id === this.botId) : null
      return (conversationBot?.type || this.bot?.type) === BOT_TYPES.EMAIL
    },

    firstRequest() {
      if (isEmpty(this.requests)) return null

      return values(this.requests).reduce((previous, current) => {
        return moment(current.created_on).isBefore(previous.created_on) ? current : previous
      }, { created_on: moment() })
    }
  },

  methods: {
    async loadConversation(contactId) {
      if (!contactId) {
        conversationDetailModule.setMessages([])
        conversationDetailModule.setRequests({})
        return
      }

      await this.loadConversationNewApi(contactId)
    },

    async loadConversationNewApi(contactId) {
      this.$emit('update:isLoading', true)
      try {
        const [fetchedRequests, fetchedMessages = []] = await Promise.all([
          this.getConversationRequests(contactId),
          this.getConversationHistory(contactId)
        ])

        conversationDetailModule.setMessages(fetchedMessages)
        conversationDetailModule.setRequests({})
        const requests = {}
        fetchedRequests.forEach(req => {
          requests[req.message_id] = req
        })
        conversationDetailModule.setRequests(requests)
        this.processMessages(this.messages, this.requests)
      } catch (err) {
        conversationDetailModule.setMessages([])
        conversationDetailModule.setRequests({})
        this.$emit('clear-bubble-selected', {})
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription, err)
      } finally {
        this.$emit('update:isLoading', false)
      }
    },

    isCarousel(content) {
      if (!content) return false
      return content.type === 'structure' && Array.isArray(content.payload)
    },

    async getConversationRequests(contactId) {
      const result = await analyticsService.getConversationRequests(contactId)
      return result.requests
    },

    async getConversationHistory(contactId) {
      const result = []
      let next = null
      let hasNext = true
      do {
        const response = await analyticsService.getConversationHistory(contactId, next)
        if (isEmpty(response.history_events)) break

        result.push(...response.history_events)
        next = response.cursor.next
        hasNext = response.cursor.has_next
      } while (hasNext)
      return result
    },

    processMessages(messages, requests) {
      // debug_step rows must be transparent to dedup/reorder below (EventDeduplicator's group
      // detection, removeFirstFindIntent, reorderFindIntentMessages all assume the array is
      // made up of real conversation messages) — strip them out first and re-insert afterward.
      // Re-insertion is position-based (how many real messages originally preceded each debug
      // step, clamped to the final length) rather than id/timestamp-based: dedup can drop the
      // messages a debug step would otherwise anchor to, and debug_step rows aren't guaranteed
      // to carry a timestamp comparable to other message types.
      const debugSteps = []
      const realMessages = []
      messages.forEach(message => {
        if (message.type === 'debug_step') {
          debugSteps.push({ precedingCount: realMessages.length, message })
        } else {
          realMessages.push(message)
        }
      })

      const messagesCopy = EventDeduplicator.deduplicate(clone(realMessages))

      let borderColor = 'transparent'
      let cachedFeedback = null
      let lastBotMessageOfRequest = null
      const feedbackOutcomes = [OUTCOME_TYPES.CUSTOMER_ANSWERED_YES, OUTCOME_TYPES.CUSTOMER_ANSWERED_NO]

      for (const message of messagesCopy) {
        const messageId = message?.data?.message_id
        const request = messageId ? requests[messageId] : null
        if (request) {
          if (cachedFeedback && lastBotMessageOfRequest) {
            lastBotMessageOfRequest.feedback = cachedFeedback
          }
          lastBotMessageOfRequest = null

          // start of a new request
          const legend = requestOutcomeMapper.mapOutcomeToLegend(request.outcome)
          borderColor = chartLabels[legend]?.color
          message.newRequestLegend = legend
          cachedFeedback = feedbackOutcomes.includes(request.outcome) ? request.outcome : null
        }

        const isBotMessage = message.data?.direction?.toLowerCase() === MESSAGE_DIRECTION.SENT
        if (isBotMessage) {
          lastBotMessageOfRequest = message
        }

        this.$set(message, 'borderColor', borderColor)
      }

      if (cachedFeedback && lastBotMessageOfRequest) {
        lastBotMessageOfRequest.feedback = cachedFeedback
      }

      this.reorderFindIntentMessages(messagesCopy)
      this.removeFirstFindIntent(messagesCopy)

      debugSteps.forEach(({ precedingCount, message }, i) => {
        const insertIndex = Math.min(precedingCount + i, messagesCopy.length)
        messagesCopy.splice(insertIndex, 0, clone(message))
      })

      conversationDetailModule.setMessages(messagesCopy)
    },

    removeFirstFindIntent(messages) {
      if (messages.length && messages[0].data?.type === ANSWER_END.FIND_INTENT) {
        messages.shift()
      }
    },

    reorderFindIntentMessages(messages) {
      let currentIndex = 1
      while (currentIndex < messages.length) {
        const findIntentIndex = findIndex(
          message => path(['data', 'type'], message) === ANSWER_END.FIND_INTENT,
          messages.slice(currentIndex)
        )

        if (findIntentIndex === -1) break

        const actualIndex = currentIndex + findIntentIndex
        const targetIndex = actualIndex - 1
        const reorderedMessages = move(actualIndex, targetIndex, messages)
        messages.splice(0, messages.length, ...reorderedMessages)
        currentIndex = actualIndex + 1
      }
    },

    getAnswer(message) {
      const intentPlatformId = this.requests[message?.data?.message_id]?.intent
      return intentPlatformId ? this.answersByIntentId[intentPlatformId] : null
    },

    getAnswerName(message) {
      return this.getAnswer(message)?.name || 'Unknown answer'
    },

    formatIntent(intent) {
      return intent.replaceAll('_', '_<wbr>')
    },

    formatTimestamp(timestamp) {
      return datetimeUtils.toMoment(timestamp).format('LL HH:mm:ss')
    },

    isCustomerBubbleFocused(messageId) {
      return this.selectedBubble ? this.selectedBubble.messageId === messageId : false
    },

    selectClientMessage(message) {
      if (!message?.data) return

      const request = this.requests[message.data.message_id]
      this.$emit('client-message-selected', {
        firstRequest: message.data.content.payload,
        subject: message.data.metadata?.subject || null,
        intent: request?.intent || null,
        messageId: message.data.message_id,
        requestId: request?.id || null,
        type: message.type
      })
    },

    async handleScrollToBubble(bubbleId) {
      if (!bubbleId) return

      const selectedBubbleElement = this.$refs[bubbleId]?.[0].$el
      selectedBubbleElement?.scrollIntoView()
    },

    getBrowserUrl(message) {
      return message.metadata?.browserUrl
    },

    selectApiAction(message) {
      this.$emit('remote-action-selected', message)
    },

    selectChatGPTAction(message) {
      this.$emit('chat-gpt-action-selected', message)
    },

    selectEvent(message) {
      this.$emit('event-selected', message)
    },

    getSelectedQuickReplyIndex(message, i) {
      if (!message.data?.content?.quick_replies) return -1

      // `i` is an index into visibleMessages (the v-for source), not messages. When the
      // debug-steps toggle is on, visibleMessages is unfiltered and a debug_step row can land
      // between the bot message and the customer's reply-click message, so skip past any
      // debug_step rows instead of assuming pure adjacency.
      const clientReply = this.visibleMessages.slice(i + 1).find(m => m.type !== 'debug_step')
      const quickReplyPayload = clientReply?.data?.content?.quick_reply?.payload
      if (!quickReplyPayload) return -1

      return message.data.content.quick_replies.findIndex(qr => qr.payload === quickReplyPayload)
    }
  }
}
</script>
